import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, MessageCircle, FileCheck2, Users, FileSignature, AlertTriangle, CheckCheck } from "lucide-react";

const TYPE_ICON = {
  chat_message: MessageCircle,
  report_completed: FileCheck2,
  report_flagged: AlertTriangle,
  household_verdict: Users,
  application_submitted: FileSignature,
};

const TYPE_TONE = {
  chat_message: "text-[#4338CA] bg-[#6366F1]/10",
  report_completed: "text-[#15803D] bg-[#22C55E]/10",
  report_flagged: "text-[#92400E] bg-[#F59E0B]/10",
  household_verdict: "text-[#0A192F] bg-[#F3EFEA]",
  application_submitted: "text-[#0A192F] bg-[#F3EFEA]",
};

function relTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const containerRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await apiClient.get("/notifications", { params: { unread_only: true, limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    const id = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    // Allow other parts of the app to nudge a refresh
    window.addEventListener("ntn:notifications:refresh", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("ntn:notifications:refresh", refresh);
    };
  }, [user]); // eslint-disable-line

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const goTo = async (n) => {
    setOpen(false);
    try { await apiClient.post("/notifications/mark-read", { ids: [n.id] }); } catch { /* noop */ }
    setUnread((u) => Math.max(0, u - 1));
    setItems((arr) => arr.filter((x) => x.id !== n.id));
    if (n.group_id) nav(`/groups/${n.group_id}`);
    else if (n.screening_id) nav(`/screenings/${n.screening_id}`);
  };

  const markAllRead = async () => {
    try {
      await apiClient.post("/notifications/mark-read", { all: true });
      setItems([]);
      setUnread(0);
    } catch { /* noop */ }
  };

  if (!user) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 flex items-center justify-center rounded-sm hover:bg-[#F3EFEA] transition-colors"
        data-testid="notification-bell"
        aria-label={`${unread} unread notifications`}
      >
        <Bell className="h-5 w-5 text-[#0A192F]" />
        {unread > 0 && (
          <span
            data-testid="notification-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-[#EF4444] rounded-full ring-2 ring-white animate-pulse"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-w-[95vw] bg-white border border-[#0A192F]/10 rounded-sm shadow-xl z-50 ntn-fade-in"
             data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#0A192F]/10">
            <div>
              <div className="font-serif text-lg text-[#0A192F]">Updates</div>
              <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#4A5568]">
                {unread} unread
              </div>
            </div>
            {unread > 0 && (
              <button onClick={markAllRead}
                className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#4A5568] hover:text-[#0A192F] flex items-center gap-1"
                data-testid="notification-mark-all-read">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#4A5568]">
              You're all caught up.
            </div>
          ) : (
            <ul className="max-h-[480px] overflow-y-auto divide-y divide-[#0A192F]/8">
              {items.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const tone = TYPE_TONE[n.type] || "text-[#0A192F] bg-[#F3EFEA]";
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => goTo(n)}
                      className="w-full text-left px-4 py-3 hover:bg-[#F3EFEA]/50 transition-colors flex items-start gap-3"
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 ${tone}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#0A192F] font-medium truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-[#4A5568] truncate mt-0.5">{n.body}</div>}
                        <div className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#4A5568] mt-1">
                          {relTime(n.created_at)}
                        </div>
                      </div>
                      <span className="h-2 w-2 rounded-full bg-[#EF4444] mt-2 shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-t border-[#0A192F]/10 px-4 py-2.5 flex items-center justify-between">
            <Link to="/dashboard" onClick={() => setOpen(false)}
              className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#0A192F] hover:underline">
              See dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Hook: builds a Set of screening ids with unread notifications + a Set of group ids
export function useUnreadIndex() {
  const { user } = useAuth();
  const [unreadByScreening, setUnreadByScreening] = useState({});
  const [unreadByGroup, setUnreadByGroup] = useState({});

  const refresh = async () => {
    try {
      const { data } = await apiClient.get("/notifications", { params: { unread_only: true, limit: 100 } });
      const bs = {};
      const bg = {};
      (data.items || []).forEach((n) => {
        if (n.screening_id) bs[n.screening_id] = (bs[n.screening_id] || []).concat(n.type);
        if (n.group_id) bg[n.group_id] = (bg[n.group_id] || []).concat(n.type);
      });
      setUnreadByScreening(bs);
      setUnreadByGroup(bg);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    const id = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("ntn:notifications:refresh", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("ntn:notifications:refresh", refresh);
    };
  }, [user]); // eslint-disable-line

  return { unreadByScreening, unreadByGroup, refresh };
}

export const NOTIFICATION_BADGE_LABEL = {
  chat_message: "NEW MESSAGE",
  report_completed: "REPORT READY",
  report_flagged: "FLAGGED",
  household_verdict: "VERDICT READY",
  application_submitted: "APPLICATION IN",
};

export const NOTIFICATION_BADGE_TONE = {
  chat_message: "bg-[#4338CA] text-white",
  report_completed: "bg-[#22C55E] text-white",
  report_flagged: "bg-[#EF4444] text-white",
  household_verdict: "bg-[#0A192F] text-white",
  application_submitted: "bg-[#F59E0B] text-white",
};
